import { normalizeHost } from './favicon';
import type { CompetitorInsight } from '../types';

/** Mirrors backend brand-name normalization for competitor matching. */
export function normalizeBrandKey(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[\u200c\u200d\u00a0]/g, ' ')
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** First label of a host, e.g. moz.com → moz (for Moz ↔ moz.com links). */
export function hostBrandLabel(input: string): string {
  const host = normalizeHost(input);
  if (!host) return '';
  const [label] = host.split('.');
  return normalizeBrandKey(label || host);
}

export interface CompetitorMatchBrand {
  knownBrandId?: string | null;
  competitorId?: string | null;
  canonicalName: string;
  domain?: string | null;
}

export function brandMatchesCompetitor(
  brand: CompetitorMatchBrand,
  competitor: CompetitorInsight,
): boolean {
  if (brand.competitorId && brand.competitorId === competitor.id) return true;

  const competitorHost = normalizeHost(competitor.host);
  const brandHost = brand.domain ? normalizeHost(brand.domain) : '';
  if (competitorHost && brandHost && competitorHost === brandHost) return true;

  const brandNorm = normalizeBrandKey(brand.canonicalName);
  const competitorNorm = normalizeBrandKey(competitor.brandName);
  if (brandNorm && competitorNorm && brandNorm === competitorNorm) return true;

  if (
    competitor.aliases?.some((alias) => normalizeBrandKey(alias.name) === brandNorm)
  ) {
    return true;
  }

  const hostLabels = new Set(
    [competitor.host, competitor.brandName, competitor.websiteUrl]
      .map((value) => hostBrandLabel(value))
      .filter(Boolean),
  );
  if (brandNorm && hostLabels.has(brandNorm)) return true;

  return false;
}

export function intelligenceRowMatchesCompetitor(
  row: { brandId?: string | null; name: string; domain?: string | null },
  competitor: CompetitorInsight,
): boolean {
  return brandMatchesCompetitor(
    {
      knownBrandId: row.brandId,
      canonicalName: row.name,
      domain: row.domain,
    },
    competitor,
  );
}

export function isTrackedCompetitorAppearing(
  competitor: CompetitorInsight,
  rows: Array<{ brandId?: string | null; name: string; domain?: string | null }>,
): boolean {
  return rows.some((row) => intelligenceRowMatchesCompetitor(row, competitor));
}
